import { ApiFilterOperation } from '@eas/common-web';
import type { TableFilter, TableFilterState } from '@eas/common-web';
import type { Department } from '@models';

export type DepartmentTableFilterSyncSignal = {
  key: number;
  departments: Department[];
};

type ApplyDepartmentTableFilterSyncParams = {
  filters: TableFilter[];
  filtersState: TableFilterState[];
  signal?: DepartmentTableFilterSyncSignal;
};

type ConsumeDepartmentTableFilterSyncParams = {
  filters: TableFilter[];
  prevFiltersState: TableFilterState[];
  signal?: DepartmentTableFilterSyncSignal;
  lastAppliedSignalKey?: number;
};

let signalKeySequence = 0;

export function createDepartmentTableFilterSyncSignal(
  departments: Department[]
): DepartmentTableFilterSyncSignal {
  signalKeySequence += 1;

  return {
    key: signalKeySequence,
    departments,
  };
}

export function applyDepartmentTableFilterSync({
  filters,
  filtersState,
  signal,
}: ApplyDepartmentTableFilterSyncParams) {
  if (!signal) {
    return {
      shouldApply: false,
      nextFiltersState: filtersState,
    };
  }

  const index = filters.findIndex(
    (filter) => filter.filterkey === 'department.id'
  );

  if (index === -1 || !filtersState[index]) {
    return {
      shouldApply: false,
      nextFiltersState: filtersState,
    };
  }

  const nextFiltersState = [...filtersState];

  nextFiltersState[index] = signal.departments.length
    ? {
        ...nextFiltersState[index],
        enabled: true,
        object: signal.departments,
        operation: ApiFilterOperation.OR,
        filters: [
          {
            operation: ApiFilterOperation.IN,
            field: 'department.id',
            values: signal.departments.map((department) => department.id),
          },
        ],
      }
    : {
        ...nextFiltersState[index],
        enabled: false,
        object: null,
        operation: ApiFilterOperation.OR,
        filters: [],
      };

  return {
    shouldApply: true,
    nextFiltersState,
  };
}

export function consumeDepartmentTableFilterSync({
  filters,
  prevFiltersState,
  signal,
  lastAppliedSignalKey,
}: ConsumeDepartmentTableFilterSyncParams) {
  if (!signal || lastAppliedSignalKey === signal.key) {
    return {
      shouldApply: false,
      shouldClearSaved: false,
      nextAppliedSignalKey: lastAppliedSignalKey,
      nextFiltersState: prevFiltersState,
    };
  }

  const result = applyDepartmentTableFilterSync({
    filters,
    filtersState: prevFiltersState,
    signal,
  });

  if (!result.shouldApply) {
    return {
      shouldApply: false,
      shouldClearSaved: false,
      nextAppliedSignalKey: lastAppliedSignalKey,
      nextFiltersState: prevFiltersState,
    };
  }

  return {
    shouldApply: true,
    shouldClearSaved: true,
    nextAppliedSignalKey: signal.key,
    nextFiltersState: result.nextFiltersState,
  };
}
